import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faPlay,
  faRefresh,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import {
  getChromaOverview,
  getChromaVectors,
  getCorpusDomainSources,
  getCorpusOverview,
  getCorpusSources,
  getPersonaPresets,
  processCorpusSources,
  uploadCorpusSources,
} from '../../services/api';
import './InterviewCorpusPage.scss';

const FALLBACK_PRESETS = [
  { id: 'telecom', label: '통신' },
  { id: 'finance', label: '금융' },
];

const PAGE_TABS = [
  { id: 'corpus', label: '코퍼스' },
  { id: 'chroma', label: 'Chroma' },
];

const SCOPE_LABELS = {
  company: '회사 전용',
  domain: '도메인 공유',
};

const STATUS_TONE = {
  uploaded: 'neutral',
  queued: 'info',
  cleaning: 'info',
  chunking: 'info',
  chunked: 'info',
  embedding: 'info',
  processing: 'info',
  embedded: 'success',
  ready: 'success',
  failed: 'danger',
};

const ACTIVE_PIPELINE_STATUSES = new Set([
  'queued',
  'cleaning',
  'chunking',
  'embedding',
  'processing',
]);

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR');
};

function InterviewCorpusPage() {
  const fileInputRef = useRef(null);
  const [pageTab, setPageTab] = useState('corpus'); // corpus | chroma
  const [browseMode, setBrowseMode] = useState('domain'); // domain | company
  const [overview, setOverview] = useState(null);
  const [companyQuery, setCompanyQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [sources, setSources] = useState([]);
  const [companyMeta, setCompanyMeta] = useState(null);
  const [domainMeta, setDomainMeta] = useState(null);
  const [presetId, setPresetId] = useState('telecom');
  const [personaPresets, setPersonaPresets] = useState(FALLBACK_PRESETS);
  const [corpusScope, setCorpusScope] = useState('company');
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingSources, setLoadingSources] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [chromaOverview, setChromaOverview] = useState(null);
  const [chromaVectors, setChromaVectors] = useState([]);
  const [chromaDomainId, setChromaDomainId] = useState('');
  const [chromaScope, setChromaScope] = useState('');
  const [chromaQuery, setChromaQuery] = useState('');
  const [loadingChroma, setLoadingChroma] = useState(false);
  const [chromaError, setChromaError] = useState(null);

  const isDomainMode = browseMode === 'domain';

  const domains = useMemo(() => {
    const rows = overview?.domains || [];
    const q = companyQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((d) => {
      const haystack = [d.label, d.id, d.host_company_name, d.hostCompanyName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [overview, companyQuery]);

  const companies = useMemo(() => {
    const rows = overview?.companies || [];
    const q = companyQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) => {
      const haystack = [
        c.display_name,
        c.displayName,
        c.name,
        c.owner_email,
        c.ownerEmail,
        String(c.id ?? ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [overview, companyQuery]);

  const companyLabel = (company) =>
    company?.display_name || company?.displayName || company?.name || '회사';

  const presetLabel = (id) => {
    const hit = personaPresets.find((p) => p.id === id);
    if (hit?.label) return hit.label;
    const domainHit = (overview?.domains || []).find((d) => d.id === id);
    return domainHit?.label || id || '—';
  };

  const hostCompanyId = useMemo(() => {
    if (!isDomainMode) return selectedCompanyId;
    return (
      domainMeta?.host_company_id
      ?? domainMeta?.hostCompanyId
      ?? domains.find((d) => d.id === selectedDomainId)?.host_company_id
      ?? domains.find((d) => d.id === selectedDomainId)?.hostCompanyId
      ?? null
    );
  }, [isDomainMode, selectedCompanyId, domainMeta, domains, selectedDomainId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPersonaPresets();
        if (!cancelled && data.persona_presets?.length) {
          setPersonaPresets(data.persona_presets);
        }
      } catch (err) {
        console.error('Persona preset 목록 조회 실패:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = overview?.totals || {};

  const fetchOverview = useCallback(async () => {
    try {
      setLoadingOverview(true);
      setError(null);
      const data = await getCorpusOverview();
      setOverview(data);

      setSelectedDomainId((prev) => {
        if (prev || !data.domains?.length) return prev;
        const first =
          data.domains.find((d) => (d.source_count ?? d.sourceCount) > 0) || data.domains[0];
        return first?.id || null;
      });

      setSelectedCompanyId((prev) => {
        if (prev || !data.companies?.length) return prev;
        const first =
          data.companies.find((c) => (c.source_count ?? c.sourceCount) > 0) || data.companies[0];
        if (first) {
          setPresetId(
            first.persona_template_id
              || first.personaTemplateId
              || first.persona_preset_id
              || first.personaPresetId
              || 'telecom'
          );
        }
        return first?.id ?? null;
      });
    } catch (err) {
      console.error(err);
      setError('코퍼스 개요를 불러오지 못했습니다.');
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const fetchCompanySources = useCallback(async (companyId, { quiet = false } = {}) => {
    if (!companyId) return;
    try {
      if (!quiet) setLoadingSources(true);
      setError(null);
      const data = await getCorpusSources(companyId);
      setSources(data.sources || []);
      setCompanyMeta(data.company || null);
      setDomainMeta(null);
      const companyTemplate =
        data.company?.persona_template_id
        || data.company?.personaTemplateId
        || data.company?.persona_preset_id
        || data.company?.personaPresetId;
      if (companyTemplate) setPresetId(companyTemplate);
      if (!quiet) setSelectedIds([]);
    } catch (err) {
      console.error(err);
      setError('인터뷰 소스 목록을 불러오지 못했습니다.');
      setSources([]);
    } finally {
      if (!quiet) setLoadingSources(false);
    }
  }, []);

  const fetchDomainSources = useCallback(async (templateId, { quiet = false } = {}) => {
    if (!templateId) return;
    try {
      if (!quiet) setLoadingSources(true);
      setError(null);
      const data = await getCorpusDomainSources(templateId);
      setSources(data.sources || []);
      setDomainMeta(data.domain || null);
      setCompanyMeta(null);
      setPresetId(templateId);
      setCorpusScope('domain');
      if (!quiet) setSelectedIds([]);
    } catch (err) {
      console.error(err);
      setError('도메인 코퍼스 목록을 불러오지 못했습니다.');
      setSources([]);
    } finally {
      if (!quiet) setLoadingSources(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (isDomainMode) {
      if (selectedDomainId) fetchDomainSources(selectedDomainId);
    } else if (selectedCompanyId) {
      fetchCompanySources(selectedCompanyId);
    }
  }, [isDomainMode, selectedDomainId, selectedCompanyId, fetchDomainSources, fetchCompanySources]);

  const hasActivePipeline = useMemo(
    () => sources.some((s) => ACTIVE_PIPELINE_STATUSES.has(s.source_status || s.sourceStatus)),
    [sources]
  );

  useEffect(() => {
    if (!hasActivePipeline) return undefined;
    const timer = setInterval(() => {
      if (isDomainMode && selectedDomainId) {
        fetchDomainSources(selectedDomainId, { quiet: true });
      } else if (!isDomainMode && selectedCompanyId) {
        fetchCompanySources(selectedCompanyId, { quiet: true });
      }
      fetchOverview();
    }, 4000);
    return () => clearInterval(timer);
  }, [
    hasActivePipeline,
    isDomainMode,
    selectedDomainId,
    selectedCompanyId,
    fetchDomainSources,
    fetchCompanySources,
    fetchOverview,
  ]);

  const handleBrowseModeChange = (mode) => {
    if (mode === browseMode) return;
    setBrowseMode(mode);
    setCompanyQuery('');
    setNotice(null);
    setError(null);
    setSelectedIds([]);
    setSources([]);
    if (mode === 'domain') {
      setCorpusScope('domain');
    }
  };

  const handleSelectCompany = (company) => {
    setSelectedCompanyId(company.id);
    setPresetId(
      company.persona_template_id
        || company.personaTemplateId
        || company.persona_preset_id
        || company.personaPresetId
        || 'telecom'
    );
    setNotice(null);
  };

  const handleSelectDomain = (domain) => {
    setSelectedDomainId(domain.id);
    setPresetId(domain.id);
    setCorpusScope('domain');
    setNotice(null);
  };

  const refreshCurrent = async () => {
    await fetchOverview();
    if (isDomainMode && selectedDomainId) {
      await fetchDomainSources(selectedDomainId);
    } else if (!isDomainMode && selectedCompanyId) {
      await fetchCompanySources(selectedCompanyId);
    }
  };

  const fetchChroma = useCallback(async () => {
    try {
      setLoadingChroma(true);
      setChromaError(null);
      const [ov, list] = await Promise.all([
        getChromaOverview(),
        getChromaVectors({
          templateId: chromaDomainId || undefined,
          corpusScope: chromaScope || undefined,
          query: chromaQuery.trim() || undefined,
          limit: 50,
          offset: 0,
        }),
      ]);
      setChromaOverview(ov.chroma || ov);
      setChromaVectors(list.vectors || []);
    } catch (err) {
      console.error(err);
      setChromaError(err.message || 'Chroma를 불러오지 못했습니다.');
      setChromaVectors([]);
    } finally {
      setLoadingChroma(false);
    }
  }, [chromaDomainId, chromaScope, chromaQuery]);

  useEffect(() => {
    if (pageTab === 'chroma') {
      fetchChroma();
    }
  }, [pageTab, fetchChroma]);

  const handleUploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    const uploadCompanyId = isDomainMode ? hostCompanyId : selectedCompanyId;
    if (!uploadCompanyId) {
      setError(
        isDomainMode
          ? '이 도메인에 호스트 회사가 없습니다. 회사 모드에서 도메인 공유로 먼저 업로드하세요.'
          : '회사를 선택하세요.'
      );
      return;
    }
    try {
      setUploading(true);
      setError(null);
      setNotice(null);
      const data = await uploadCorpusSources(uploadCompanyId, {
        files: Array.from(fileList),
        presetId: isDomainMode ? selectedDomainId : presetId,
        templateId: isDomainMode ? selectedDomainId : presetId,
        corpusScope: isDomainMode ? 'domain' : corpusScope,
      });
      setNotice(data.message || `${data.created_count || data.createdCount || 0}개 업로드 완료`);
      await refreshCurrent();
    } catch (err) {
      console.error(err);
      setError(err.message || '업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleProcess = async () => {
    const processCompanyId = isDomainMode ? hostCompanyId : selectedCompanyId;
    if (!processCompanyId) {
      setError(
        isDomainMode
          ? '처리할 호스트 회사가 없습니다.'
          : '회사를 선택하세요.'
      );
      return;
    }
    try {
      setProcessing(true);
      setError(null);
      setNotice(null);
      const data = await processCorpusSources(processCompanyId, {
        sourceIds: selectedIds.length ? selectedIds : undefined,
      });
      setNotice(data.message || '파이프라인 대기열에 등록했습니다.');
      await refreshCurrent();
    } catch (err) {
      console.error(err);
      setError(err.message || '처리 요청에 실패했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === sources.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sources.map((s) => s.id));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUploadFiles(e.dataTransfer.files);
  };

  const hasSelection = isDomainMode ? Boolean(selectedDomainId) : Boolean(selectedCompanyId);
  const mainTitle = isDomainMode
    ? (domainMeta?.label || selectedDomainId || '도메인')
    : (companyLabel(companyMeta) || '회사');
  const canUpload = isDomainMode ? Boolean(hostCompanyId) : Boolean(selectedCompanyId);

  return (
    <div className="interview-corpus-page">
      <div className="sa-page-header">
        <div className="sa-page-header-left">
          <h1 className="sa-page-title">Interview Corpus</h1>
          <p className="sa-page-subtitle">
            Postgres 인터뷰 소스와 Chroma 벡터를 한곳에서 확인하는 작업 공간
          </p>
        </div>
        <button
          type="button"
          className="sa-btn sa-btn-secondary"
          onClick={() => {
            if (pageTab === 'chroma') fetchChroma();
            else refreshCurrent();
          }}
          disabled={pageTab === 'chroma' ? loadingChroma : (loadingOverview || loadingSources)}
        >
          <FontAwesomeIcon icon={faRefresh} />
          새로고침
        </button>
      </div>

      <div className="page-tabs" role="tablist" aria-label="Interview Corpus 탭">
        {PAGE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={pageTab === tab.id}
            className={pageTab === tab.id ? 'active' : ''}
            onClick={() => {
              setPageTab(tab.id);
              setError(null);
              setNotice(null);
              setChromaError(null);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {pageTab === 'chroma' ? (
        <div className="chroma-panel">
          {(chromaError) && (
            <div className="corpus-banner error">{chromaError}</div>
          )}
          <div className="corpus-stats">
            <div className="corpus-stat">
              <span className="label">Chroma Total</span>
              <strong>{chromaOverview?.total ?? '—'}</strong>
            </div>
            <div className="corpus-stat">
              <span className="label">Interview Vectors</span>
              <strong>
                {chromaOverview?.interview_evidence_total
                  ?? chromaOverview?.interviewEvidenceTotal
                  ?? '—'}
              </strong>
            </div>
            <div className="corpus-stat">
              <span className="label">Collection</span>
              <strong style={{ fontSize: 14 }}>{chromaOverview?.collection || 'ux_rag'}</strong>
            </div>
            <div className="corpus-stat">
              <span className="label">Path</span>
              <strong style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {chromaOverview?.path || '—'}
              </strong>
            </div>
          </div>

          <div className="chroma-filters">
            <div className="field-group">
              <label htmlFor="chroma-domain">도메인</label>
              <select
                id="chroma-domain"
                value={chromaDomainId}
                onChange={(e) => setChromaDomainId(e.target.value)}
              >
                <option value="">전체</option>
                {(chromaOverview?.domains || []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label || d.id} ({d.count})
                  </option>
                ))}
              </select>
            </div>
            <div className="field-group">
              <label htmlFor="chroma-scope">Scope</label>
              <select
                id="chroma-scope"
                value={chromaScope}
                onChange={(e) => setChromaScope(e.target.value)}
              >
                <option value="">전체</option>
                <option value="domain">domain</option>
                <option value="company">company</option>
              </select>
            </div>
            <div className="field-group grow">
              <label htmlFor="chroma-query">유사도 검색 (선택)</label>
              <input
                id="chroma-query"
                type="text"
                placeholder="쿼리 넣으면 Chroma query로 검색"
                value={chromaQuery}
                onChange={(e) => setChromaQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') fetchChroma();
                }}
              />
            </div>
            <button
              type="button"
              className="sa-btn sa-btn-primary"
              onClick={fetchChroma}
              disabled={loadingChroma}
            >
              <FontAwesomeIcon icon={faSearch} />
              조회
            </button>
          </div>

          <div className="chroma-type-row">
            {Object.entries(chromaOverview?.by_data_type || chromaOverview?.byDataType || {}).map(
              ([key, value]) => (
                <span key={key} className="pipeline-chip">
                  {key}: {value}
                </span>
              )
            )}
          </div>

          <div className="corpus-table-wrap">
            <div className="table-caption">
              <span>Vectors ({chromaVectors.length})</span>
              {loadingChroma && <span className="muted">불러오는 중…</span>}
            </div>
            <table className="sa-table corpus-table">
              <thead>
                <tr>
                  <th>도메인</th>
                  <th>Scope</th>
                  <th>소스</th>
                  <th>target vars</th>
                  <th>문서 미리보기</th>
                  <th>거리</th>
                </tr>
              </thead>
              <tbody>
                {!loadingChroma && chromaVectors.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty-cell">
                      조건에 맞는 Chroma 벡터가 없습니다.
                    </td>
                  </tr>
                )}
                {chromaVectors.map((row) => (
                  <tr key={row.id}>
                    <td>{row.template_id || row.templateId || '—'}</td>
                    <td>{row.corpus_scope || row.corpusScope || '—'}</td>
                    <td>
                      <div className="source-title">
                        {row.source_title || row.sourceTitle || '—'}
                      </div>
                      <div className="source-sub">
                        source {row.source_id || row.sourceId || '—'}
                        {' · '}
                        chunk {row.chunk_db_id || row.chunkDbId || '—'}
                        {' · '}
                        company {row.company_id || row.companyId || '—'}
                      </div>
                    </td>
                    <td>
                      <div className="source-sub" title={row.target_variables || row.targetVariables || ''}>
                        {row.target_variables || row.targetVariables || '—'}
                      </div>
                    </td>
                    <td>
                      <div className="chroma-doc" title={row.document || ''}>
                        {(row.document || '').slice(0, 180) || '—'}
                        {(row.document || '').length > 180 ? '…' : ''}
                      </div>
                    </td>
                    <td>
                      {row.distance == null
                        ? '—'
                        : (typeof row.distance === 'number' ? row.distance.toFixed(4) : row.distance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
      <div className="corpus-stats">
        <div className="corpus-stat">
          <span className="label">Sources</span>
          <strong>{totals.sources ?? '—'}</strong>
        </div>
        <div className="corpus-stat">
          <span className="label">Chunks</span>
          <strong>{totals.chunks ?? '—'}</strong>
        </div>
        <div className="corpus-stat">
          <span className="label">Embedded</span>
          <strong>{totals.embedded_chunks ?? totals.embeddedChunks ?? '—'}</strong>
        </div>
        <div className="corpus-stat pipeline">
          <span className="label">Pipeline</span>
          <div className="pipeline-steps">
            {['clean', 'chunk', 'embed'].map((step) => (
              <span key={step} className="pipeline-chip">
                {step}
              </span>
            ))}
          </div>
        </div>
      </div>

      {(error || notice) && (
        <div className={`corpus-banner ${error ? 'error' : 'ok'}`}>
          {error || notice}
        </div>
      )}

      <div className="corpus-workspace">
        <aside className="corpus-company-panel">
          <div className="browse-mode-toggle" role="tablist" aria-label="코퍼스 보기 모드">
            <button
              type="button"
              role="tab"
              aria-selected={isDomainMode}
              className={isDomainMode ? 'active' : ''}
              onClick={() => handleBrowseModeChange('domain')}
            >
              도메인
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isDomainMode}
              className={!isDomainMode ? 'active' : ''}
              onClick={() => handleBrowseModeChange('company')}
            >
              회사
            </button>
          </div>
          <div className="panel-title">{isDomainMode ? '도메인 선택' : '회사 선택'}</div>
          <div className="company-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder={isDomainMode ? '도메인 검색' : '회사명 / 유저명 검색'}
              value={companyQuery}
              onChange={(e) => setCompanyQuery(e.target.value)}
            />
          </div>
          <div className="company-list">
            {loadingOverview && <div className="empty">불러오는 중…</div>}
            {isDomainMode ? (
              <>
                {!loadingOverview && domains.length === 0 && (
                  <div className="empty">도메인이 없습니다.</div>
                )}
                {domains.map((domain) => {
                  const active = domain.id === selectedDomainId;
                  return (
                    <button
                      key={domain.id}
                      type="button"
                      className={`company-row ${active ? 'active' : ''}`}
                      onClick={() => handleSelectDomain(domain)}
                    >
                      <div className="name">{domain.label || domain.id}</div>
                      <div className="meta">
                        <span>{domain.id}</span>
                        <span>
                          {(domain.source_count ?? domain.sourceCount ?? 0)} sources
                          {(domain.host_company_name || domain.hostCompanyName)
                            ? ` · ${domain.host_company_name || domain.hostCompanyName}`
                            : ''}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </>
            ) : (
              <>
                {!loadingOverview && companies.length === 0 && (
                  <div className="empty">회사가 없습니다.</div>
                )}
                {companies.map((company) => {
                  const active = company.id === selectedCompanyId;
                  return (
                    <button
                      key={company.id}
                      type="button"
                      className={`company-row ${active ? 'active' : ''}`}
                      onClick={() => handleSelectCompany(company)}
                    >
                      <div className="name">{companyLabel(company)}</div>
                      <div className="meta">
                        <span>
                          {presetLabel(
                            company.persona_template_id
                              || company.personaTemplateId
                              || company.persona_preset_id
                              || company.personaPresetId
                          )}
                        </span>
                        <span>
                          id {company.id} · {company.source_count ?? company.sourceCount ?? 0} sources
                        </span>
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </aside>

        <section className="corpus-main">
          {!hasSelection ? (
            <div className="empty-main">
              {isDomainMode ? '왼쪽에서 도메인을 선택하세요.' : '왼쪽에서 회사를 선택하세요.'}
            </div>
          ) : (
            <>
              <div className="corpus-toolbar">
                <div className="toolbar-left">
                  <h2>{mainTitle}</h2>
                  {isDomainMode ? (
                    <div className="field-group domain-hint">
                      <span className="hint-pill">도메인 공유</span>
                      {hostCompanyId ? (
                        <span className="hint-text">
                          호스트 회사 id {hostCompanyId}
                          {(domainMeta?.host_company_name || domainMeta?.hostCompanyName)
                            ? ` · ${domainMeta.host_company_name || domainMeta.hostCompanyName}`
                            : ''}
                        </span>
                      ) : (
                        <span className="hint-text warn">
                          호스트 회사 없음 — 회사 모드에서 도메인 공유로 먼저 업로드
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="field-group">
                        <label htmlFor="corpus-preset">도메인</label>
                        <select
                          id="corpus-preset"
                          value={presetId}
                          onChange={(e) => setPresetId(e.target.value)}
                        >
                          {personaPresets.map((preset) => (
                            <option key={preset.id} value={preset.id}>
                              {preset.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field-group">
                        <label htmlFor="corpus-scope">Scope</label>
                        <select
                          id="corpus-scope"
                          value={corpusScope}
                          onChange={(e) => setCorpusScope(e.target.value)}
                        >
                          <option value="company">회사 전용</option>
                          <option value="domain">도메인 공유</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
                <div className="toolbar-right">
                  <button
                    type="button"
                    className="sa-btn sa-btn-primary"
                    onClick={handleProcess}
                    disabled={
                      processing
                      || loadingSources
                      || sources.length === 0
                      || (isDomainMode && !hostCompanyId)
                    }
                  >
                    <FontAwesomeIcon icon={faPlay} />
                    {processing
                      ? '시작 중…'
                      : selectedIds.length
                        ? `선택 ${selectedIds.length}개 처리`
                        : hasActivePipeline
                          ? '추가 처리 / 재시도'
                          : '업로드분 처리'}
                  </button>
                </div>
              </div>

              <div
                className={`corpus-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'busy' : ''} ${!canUpload ? 'disabled' : ''}`}
                onDragOver={(e) => {
                  if (!canUpload) return;
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={canUpload ? onDrop : undefined}
                onClick={() => {
                  if (canUpload) fileInputRef.current?.click();
                }}
                role="button"
                tabIndex={canUpload ? 0 : -1}
                aria-disabled={!canUpload}
                onKeyDown={(e) => {
                  if (!canUpload) return;
                  if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                }}
              >
                <FontAwesomeIcon icon={faCloudUploadAlt} />
                <div className="drop-title">
                  {uploading
                    ? '업로드 중…'
                    : canUpload
                      ? '인터뷰 .txt 파일을 드롭하거나 클릭해서 업로드'
                      : '호스트 회사가 없어 업로드할 수 없습니다'}
                </div>
                <div className="drop-sub">
                  현재 설정:{' '}
                  {isDomainMode
                    ? `${presetLabel(selectedDomainId)} · 도메인 공유`
                    : `${presetLabel(presetId)} · ${SCOPE_LABELS[corpusScope] || corpusScope}`}
                  {' · '}원문은 비어 있지 않은 줄 20줄 이상 권장
                </div>
                {hasActivePipeline ? (
                  <div className="drop-sub" style={{ marginTop: 6, color: '#1d4ed8' }}>
                    파이프라인 실행 중 — 상태를 자동으로 새로고침합니다
                  </div>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,text/plain"
                  multiple
                  hidden
                  onChange={(e) => handleUploadFiles(e.target.files)}
                />
              </div>

              <div className="corpus-table-wrap">
                <div className="table-caption">
                  <span>Sources ({sources.length})</span>
                  {loadingSources && <span className="muted">불러오는 중…</span>}
                </div>
                <table className="sa-table corpus-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          checked={sources.length > 0 && selectedIds.length === sources.length}
                          onChange={toggleSelectAll}
                          aria-label="전체 선택"
                        />
                      </th>
                      <th>제목</th>
                      {isDomainMode ? <th>호스트</th> : null}
                      <th>상태</th>
                      <th>Chunks</th>
                      <th>Embedded</th>
                      <th>길이</th>
                      <th>업데이트</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.length === 0 && !loadingSources && (
                      <tr>
                        <td colSpan={isDomainMode ? 8 : 7} className="empty-cell">
                          아직 업로드된 인터뷰가 없습니다. 위에서 .txt를 올려 시작하세요.
                        </td>
                      </tr>
                    )}
                    {sources.map((source) => {
                      const status = source.source_status || source.sourceStatus;
                      const tone = STATUS_TONE[status] || 'neutral';
                      return (
                        <tr key={source.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(source.id)}
                              onChange={() => toggleSelect(source.id)}
                              aria-label={`${source.title} 선택`}
                            />
                          </td>
                          <td>
                            <div className="source-title">{source.title}</div>
                            {source.participant_code || source.participantCode ? (
                              <div className="source-sub">
                                {source.participant_code || source.participantCode}
                              </div>
                            ) : null}
                          </td>
                          {isDomainMode ? (
                            <td>
                              <div className="source-sub">
                                {source.company_name || source.companyName || `id ${source.company_id || source.companyId || '—'}`}
                              </div>
                            </td>
                          ) : null}
                          <td>
                            <span className={`status-pill tone-${tone}`}>
                              {source.status_label || source.statusLabel || status}
                            </span>
                            {(source.processing_error || source.processingError) ? (
                              <div className="source-error" title={source.processing_error || source.processingError}>
                                {source.processing_error || source.processingError}
                              </div>
                            ) : null}
                          </td>
                          <td>{source.chunk_count ?? source.chunkCount ?? 0}</td>
                          <td>{source.embedded_count ?? source.embeddedCount ?? 0}</td>
                          <td>{(source.raw_text_length ?? source.rawTextLength ?? 0).toLocaleString()}</td>
                          <td>{formatDate(source.updated_at || source.updatedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
        </>
      )}
    </div>
  );
}

export default InterviewCorpusPage;
